import { useState } from "react";
import { useProductApi } from "@/hooks/apiHooks/useProductApi";
import type { ProductCoa } from "@models/product";
import { asApiFailure } from "@utils/apiFailure";
import { saveBlob } from "@utils/fileDownload";

interface ProductCoaDownloadProps {
    productId: string;
    coa: ProductCoa;
}

type DownloadState =
    | { phase: "idle" }
    | { phase: "downloading" }
    | { phase: "saved"; fileName: string }
    | { phase: "failed"; message: string };

// Inline copy for the failures of GET /products/{id}/coa. The API's own 403
// (the active-customer wall: a suspended or lapsed account) and 404 (no COA
// any more, or its file is gone) reach here untoasted, as fetchDownload's
// "contracted" mode leaves them, and get their own sentence. Everything else
// gets the generic one.
const failureMessage = (error: unknown): string => {
    switch (asApiFailure(error)?.status) {
        case 403:
            return "You don't have permission to download this certificate.";

        case 404:
            return "This certificate is no longer available.";

        default:
            return "Couldn't download the certificate.";
    }
};

// The product's Certificate of Authenticity (#476, DecisionLog I3), for the
// customer. The file is private, so the bytes come through the API (bearer
// token) and are saved client-side, as the admin Edit page and the invoice
// download do. The download says it is in flight, then that it saved or
// failed, never nothing. Of the failures that get the generic sentence,
// useApi has also toasted the request's own (any other status, a 403 or 404
// the API did not write, or no response); a 2xx that is not a file, or a body
// that fails to read, has no toast.
// The file is saved whenever the download lands, even after this control has
// unmounted (Product Details collapsed, or the page moved on): the shopper
// asked for it. A state update after unmount is ignored.
export const ProductCoaDownload = ({
    productId,
    coa,
}: ProductCoaDownloadProps) => {
    const { downloadCoa } = useProductApi();
    const [state, setState] = useState<DownloadState>({ phase: "idle" });

    const download = async () => {
        setState({ phase: "downloading" });

        try {
            const downloaded = await downloadCoa(productId);

            // The server's Content-Disposition name wins.
            const fileName = downloaded.fileName ?? coa.fileName;
            saveBlob(downloaded.blob, fileName);
            setState({ phase: "saved", fileName });
        } catch (error) {
            setState({ phase: "failed", message: failureMessage(error) });
        }
    };

    const isDownloading = state.phase === "downloading";
    const status =
        state.phase === "downloading"
            ? `Downloading ${coa.fileName}…`
            : state.phase === "saved"
                ? `${state.fileName} downloaded.`
                : "";

    return (
        <div className="flex flex-col items-start">
            <button
                type="button"
                onClick={() => void download()}
                disabled={isDownloading}
                className="btn btn-link btn-sm px-0 text-info no-underline hover:underline"
            >
                {isDownloading && (
                    <span
                        className="loading loading-spinner loading-xs"
                        aria-hidden="true"
                    />
                )}
                Download Certificate of Authenticity
            </button>
            {/* Always present, so the live region is there before its text
                is. The margin only with text: an empty paragraph's margin
                would still take space while idle. */}
            <p
                aria-live="polite"
                className={`${status ? "mt-1 " : ""}text-sm text-neutral/70`}
            >
                {status}
            </p>
            {state.phase === "failed" && (
                <p role="alert" className="mt-1 text-sm font-semibold text-error">
                    {state.message}
                </p>
            )}
        </div>
    );
};
